import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { delay } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { TornPaper } from '@/components/generals/paper'
import { Photo } from '@/components/generals/photo'
import { Ambient } from '@/components/generals/backdrops'
import { Container } from '@/components/generals/container'
import { routes } from '@/constants/site'

const PATHS = [
    {
        title: 'Hiring a designer?',
        body: 'Start a search and your team workspace opens with your first brief.',
        label: 'Get started',
        href: routes.getStarted,
        photo: 'nora' as const
    },
    {
        title: 'Want to scout?',
        body: 'Apply to join the network. Approved scouts receive a private invitation.',
        label: 'Become a scout',
        href: routes.applyToScout,
        photo: 'elena' as const
    }
]

const LogIn: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Log in'
                description='Kerning workspaces for hiring teams and scouts open by private invitation.'
            />

            <section id='log-in' className='pt-hero relative isolate overflow-hidden pb-14 sm:pb-[90px]'>
                <Ambient field className='-z-10' />

                <Container className='flex flex-col items-center text-center'>
                    <h1 className='type-hero enter' style={delay(0)}>
                        Welcome back
                    </h1>

                    <p className='type-lead enter mt-5 max-w-[46ch]' style={delay(100)}>
                        Workspaces for hiring teams and scouts open by private invitation. Yours arrives at the email you applied with, as soon as your first search or scouting agreement begins.
                    </p>

                    <ul className='enter mt-12 grid w-full max-w-[680px] gap-5 text-left sm:grid-cols-2' style={delay(220)}>
                        {PATHS.map(path => (
                            <li key={path.href}>
                                <Link href={path.href} className='group block h-full rounded-sm'>
                                    <TornPaper className='flex h-full flex-col p-7' surfaceClassName='transition-colors duration-300 group-hover:bg-paper'>
                                        <Photo id={path.photo} sizes='44px' portrait className='size-11 shrink-0 rounded-full shadow-[0_0_0_2.5px_#fffdf8,0_3px_8px_-3px_rgb(40_38_34/0.4)]' />
                                        <span className='type-card mt-7'>
                                            {path.title}
                                        </span>
                                        <span className='mt-2 text-ink/75'>
                                            {path.body}
                                        </span>
                                        <span className='mt-6 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-ink'>
                                            {path.label}
                                            <ArrowRight aria-hidden='true' strokeWidth={1.75} className='size-4 transition-transform duration-500 ease-soft group-hover:translate-x-0.5' />
                                        </span>
                                    </TornPaper>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </Container>
            </section>
        </Layout>
    )
}

export default LogIn
